import React, { useRef, useCallback, useState, ChangeEvent, useEffect } from "react";
import { FormHandles, useField } from "@unform/core";
import { Form } from "@unform/web";
import { Link, useHistory, useLocation } from "react-router-dom";
import * as Yup from "yup";
import getValidationErrors from "../../../Utils/getValidationErrors";
import { useAuth } from "../../../Contexts/AuthContext";
import {
  FiMail,
  FiLock,
  FiArrowRight,
  FiShield,
  FiEye,
  FiEyeOff,
  FiInfo,
  FiX,
} from "react-icons/fi";
import intl from "react-intl-universal";
import { SESSION_DISCONNECTED_KEY } from "../../../Contexts/AuthContext";
import { CompanyLogo } from "../../../Components/Atoms/CompanyLogo";
import {
  normalizeFirstAccessInput,
  prepareUsernameForApi,
} from "../MyFirstAccess/utils";
import { loadItem } from "../../../Utils";
import { fetchInstanceBranding } from "../../../Hooks/WhiteLabel";
import { isPartnerBrand, partnerBrandLabel } from "../loginPartnerTheme";

/** Partner white-label when DNS resolved a non-matrix instance. */
const shouldForceValutLogo = () => {
  const options = loadItem("Options") || {};
  return !isPartnerBrand(options);
};

const LoginUsernameField = ({
  inputMask,
  setInputMask,
}: {
  inputMask: string;
  setInputMask: (value: string) => void;
}) => {
  const { fieldName, error, registerField, clearError } = useField("username");

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => inputMask,
      clearValue: () => setInputMask(""),
    });
  }, [fieldName, registerField, inputMask, setInputMask]);

  return (
    <div className="login-field-group">
      <label className="login-field-label" htmlFor="username">
        {intl.get("auth.documentOrEmail")}
      </label>
      <div className="login-field">
        <span className="login-field__icon">
          <FiMail />
        </span>
        <input
          id="username"
          name="username"
          type="text"
          placeholder={intl.get("auth.documentOrEmailPlaceholder")}
          value={inputMask}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            clearError();
            setInputMask(normalizeFirstAccessInput(e.currentTarget.value));
          }}
          onFocus={clearError}
          className="login-field__input"
          autoComplete="username"
        />
      </div>
      {error ? <p className="login-field__error">{error}</p> : null}
    </div>
  );
};

const LoginPasswordField = ({
  password,
  setPassword,
}: {
  password: string;
  setPassword: (value: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { fieldName, error, registerField, clearError } = useField("password");

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => password,
      clearValue: () => setPassword(""),
    });
  }, [fieldName, registerField, password, setPassword]);

  useEffect(() => {
    const syncAutofill = () => {
      const currentValue = inputRef.current?.value ?? "";
      if (currentValue && currentValue !== password) {
        setPassword(currentValue);
      }
    };

    syncAutofill();
    const timer = window.setTimeout(syncAutofill, 300);

    return () => window.clearTimeout(timer);
  }, [password, setPassword]);

  return (
    <>
      <div className="login-field login-field--password">
        <span className="login-field__icon">
          <FiLock />
        </span>
        <input
          ref={inputRef}
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          value={password}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            clearError();
            setPassword(e.currentTarget.value);
          }}
          onInput={(e: ChangeEvent<HTMLInputElement>) => {
            setPassword(e.currentTarget.value);
          }}
          onFocus={clearError}
          className="login-field__input login-field__input--password"
          autoComplete="current-password"
        />
        <button
          type="button"
          className="login-field__toggle"
          onClick={() => setShowPassword((prev) => !prev)}
          onMouseDown={(event) => event.preventDefault()}
          aria-label={
            showPassword ? intl.get("auth.hidePassword") : intl.get("auth.showPassword")
          }
        >
          {showPassword ? <FiEyeOff /> : <FiEye />}
        </button>
      </div>
      {error ? <p className="login-field__error">{error}</p> : null}
    </>
  );
};

const Login: React.FC = () => {
  const options = loadItem("Options") || {};
  const isPartner = isPartnerBrand(options);
  const brandLabel = partnerBrandLabel(options);
  const formRef = useRef<FormHandles>(null);
  const [inputMask, setInputMask] = useState("");
  const [password, setPassword] = useState("");
  const { auth, isLoading } = useAuth();
  const location = useLocation();
  const history = useHistory();
  const [showSessionAlert, setShowSessionAlert] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery =
      params.get("session") === "expired" || params.has("disconnected");
    const fromState = Boolean(
      (location.state as { sessionDisconnected?: boolean } | null)?.sessionDisconnected
    );
    const fromStorage = sessionStorage.getItem(SESSION_DISCONNECTED_KEY) === "1";

    if (!fromQuery && !fromState && !fromStorage) {
      return;
    }

    setShowSessionAlert(true);
    // Consume one-shot signals so refresh / revisit won't re-show the banner.
    sessionStorage.removeItem(SESSION_DISCONNECTED_KEY);
    if (fromQuery || fromState) {
      history.replace({ pathname: location.pathname });
    }
  }, [history, location.pathname, location.search, location.state]);

  const handleSubmit = useCallback(async () => {
    const payload = {
      username: prepareUsernameForApi(inputMask),
      password,
    };

    const authSchema = Yup.object().shape({
      username: Yup.string().required(intl.get("auth.documentOrEmailRequired")),
      password: Yup.string().min(8, intl.get("auth.passwordRequired")),
    });

    try {
      await authSchema.validate(payload, {
        abortEarly: false,
      });

      await auth({
        username: payload.username,
        password: payload.password,
      });
      // Optional authenticated branding refresh (host still wins; no-op on failure).
      fetchInstanceBranding({ authenticated: true }).catch(() => {});
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
      }
    }
  }, [auth, inputMask, password]);

  return (
    <div className="login-glass-card">
      {showSessionAlert ? (
        <div className="login-session-alert" role="status">
          <span className="login-session-alert__icon" aria-hidden="true">
            <FiInfo />
          </span>
          <div className="login-session-alert__content">
            <p className="login-session-alert__title">
              {intl.get("auth.sessionDisconnectedTitle")}
            </p>
            <p className="login-session-alert__body">
              {intl.get("auth.sessionDisconnectedBody")}
            </p>
          </div>
          <button
            type="button"
            className="login-session-alert__close"
            onClick={() => setShowSessionAlert(false)}
            aria-label={intl.get("auth.closeAlert")}
          >
            <FiX />
          </button>
        </div>
      ) : null}

      <div className="login-glass-card__brand">
        <CompanyLogo
          forceValut={shouldForceValutLogo()}
          onDark
          variant="gold"
          width={168}
          height={32}
        />
      </div>

      <div className="login-glass-card__header">
        <h1 className="login-glass-card__title">{intl.get("auth.welcomeBack")}</h1>
        <p className="login-glass-card__subtitle">
          {isPartner
            ? intl.get("auth.signInSubtitlePartner", { brand: brandLabel })
            : intl.get("auth.signInSubtitle")}
        </p>
      </div>

      <Form ref={formRef} onSubmit={handleSubmit} className="login-form">
        <LoginUsernameField inputMask={inputMask} setInputMask={setInputMask} />

        <div className="login-field-group">
          <div className="login-field-label-row">
            <label className="login-field-label" htmlFor="password">
              {intl.get("words.password")}
            </label>
            <Link to="/auth/forgot-password" className="login-link-muted">
              {intl.get("auth.forgotPassword")}
            </Link>
          </div>
          <LoginPasswordField password={password} setPassword={setPassword} />
        </div>

        <button
          type="submit"
          className="login-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="login-submit-btn__spinner" />
              {intl.get("auth.signingIn")}
            </>
          ) : (
            <>
              {intl.get("auth.signIn")}
              <FiArrowRight />
            </>
          )}
        </button>
      </Form>

      <div className="login-security-badges">
        {[
          intl.get("auth.ssl256"),
          intl.get("auth.bankEncryption"),
          intl.get("auth.infrastructureSocTypeIi"),
        ].map((label) => (
          <div key={label} className="login-security-badge">
            <FiShield />
            <span>{label}</span>
          </div>
        ))}
      </div>

      <p className="login-glass-card__cta">
        {intl.get("auth.noAccount")}{" "}
        <Link to="/auth/my-first-access" className="login-glass-card__cta-link">
          {intl.get("auth.requestAccess")}
        </Link>
      </p>
    </div>
  );
};

export default Login;
